import { Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { TextSemibold14 } from 'styles/typography';
import { toFixedIfFloat } from 'utils/helpers';
import { ICultureListItem } from '../../slice/types';

interface IProps {
  culturesArr: ICultureListItem[];
  totalArea: number;
  companyId?: number;
}

const CulturesTable: FC<IProps> = ({ culturesArr, totalArea, companyId }) => {
  const { t } = useTranslation();

  const envId = useSelector(selectSelectedEnvId);

  const history = useHistory();

  const columns = useMemo(
    (): ColumnsType<ICultureListItem> => [
      {
        title: t('Cultures.List.Table.Culture', 'Culture'),
        dataIndex: 'cultureName',
        key: 'cultureName',
        width: '70%',
        render: (cultureName: string) => cultureName,
        sorter: {
          compare: (a: ICultureListItem, b: ICultureListItem) => {
            return a.cultureName.localeCompare(b.cultureName);
          },
        },
      },
      {
        title: t('Cultures.List.Table.FieldArea', 'Field area (ha)'),
        dataIndex: 'area',
        key: 'area',
        width: '30%',
        render: (area: number) => toFixedIfFloat(area),
        sorter: {
          compare: (a: ICultureListItem, b: ICultureListItem) => {
            return Number(a.area) - Number(b.area);
          },
        },
      },
    ],
    [t],
  );

  const openCultureDetails = useCallback(
    (cultureId: number, companyId?: number) => () => {
      if (envId) {
        let url = '';
        if (companyId) {
          url = generatePath(RouteConstants.cultures.detailsByCompany, {
            envId: Number(envId),
            cultureId,
            companyId,
          });
        } else {
          url = generatePath(RouteConstants.cultures.details, {
            envId: Number(envId),
            cultureId,
          });
        }
        history.push(url);
      }
    },
    [envId, history],
  );

  const renderTableFooterTotalArea = useCallback(
    (area: number) => {
      return (
        <Row justify="end">
          <TextSemibold14>
            {t('Cultures.List.Table.FooterTotalArea', {
              defaultValue: 'Total: {{area}} ha',
              area: toFixedIfFloat(area),
            })}
          </TextSemibold14>
        </Row>
      );
    },
    [t],
  );

  return (
    <StyledTable
      rowKey="cultureId"
      onRow={cultureItem => {
        return {
          onClick: openCultureDetails(cultureItem.cultureId, companyId),
        };
      }}
      dataSource={culturesArr}
      columns={columns}
      pagination={false}
      bordered={true}
      footer={() => renderTableFooterTotalArea(totalArea)}
    />
  );
};

export default CulturesTable;
